import React, { useState, useEffect } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Grid,
  Alert,
  CircularProgress,
  useTheme,
  Avatar,
  Divider,
} from "@mui/material";
import {
  Visibility as ViewIcon,
  CheckCircle as ReadIcon,
  RadioButtonUnchecked as UnreadIcon,
  Search as SearchIcon,
  Person as PersonIcon,
  ArrowForward as ArrowForwardIcon,
} from "@mui/icons-material";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { DeleteIcon, RefreshCwIcon } from "lucide-react";
import Engagement from "../../components/Engagement";

const API_URL =
  process.env.REACT_APP_PRODUCTION_SERVER || "https://admin.clikkle.com:5100";

const COLORS = [
  "#0088FE",
  "#00C49F",
  "#FFBB28",
  "#FF8042",
  "#ab47bc",
  "#29b6f6",
];

function Feedback() {
  const navigate = useNavigate();
  const [feedbacks, setFeedbacks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const theme = useTheme();
  const [searchQuery, setSearchQuery] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [readFilter, setReadFilter] = useState("");
  const [appNameFilter, setAppNameFilter] = useState("");

  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const [adminNote, setAdminNote] = useState("");

  const [userDetailsOpen, setUserDetailsOpen] = useState(false);
  const [userDetails, setUserDetails] = useState(null);
  const [userDetailsLoading, setUserDetailsLoading] = useState(false);
  const [userDetailsError, setUserDetailsError] = useState("");

  const location = useLocation();

  const queryParams = new URLSearchParams(location.search);
  const feedbackIdFromUrl = queryParams.get("id");

  useEffect(() => {
    if (feedbackIdFromUrl) {
      fetchFeedbackById(feedbackIdFromUrl);
    }
  }, [feedbackIdFromUrl]);

  const handleUserDetailsClick = (userId) => {
    setUserDetailsOpen(true);
    fetchUserDetails(userId);
  };

  const handleCloseUserDetails = () => {
    setUserDetailsOpen(false);
    setUserDetails(null);
    setUserDetailsError("");
  };

  const fetchFeedbacks = async () => {
    setLoading(true);
    setError("");

    try {
      const params = new URLSearchParams({
        page: page + 1,
        limit: rowsPerPage,
      });

      if (searchQuery) params.append("q", searchQuery);
      if (categoryFilter) params.append("category", categoryFilter);
      if (readFilter !== "") params.append("read", readFilter);
      if (appNameFilter) params.append("appName", appNameFilter);

      const response = await axios.get(
        `${API_URL}/api/adminfeedback?${params}`,
        {
          headers: {
            Authorization: `Bearer ${getAuthToken()}`,
          },
        }
      );

      setFeedbacks(response.data.data.rows);
      setTotal(response.data.data.total);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to fetch feedbacks");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFeedbacks();
  }, [
    page,
    rowsPerPage,
    searchQuery,
    categoryFilter,
    readFilter,
    appNameFilter,
  ]);

  const handleToggleRead = async (id, currentReadStatus) => {
    try {
      await axios.patch(
        `${API_URL}/api/adminfeedback/${id}`,
        { read: !currentReadStatus },
        {
          headers: {
            Authorization: `Bearer ${getAuthToken()}`,
          },
        }
      );
      fetchFeedbacks();
    } catch (err) {
      setError("Failed to update feedback");
    }
  };

  const handleViewDetails = async (feedback) => {
    setSelectedFeedback(feedback);
    setAdminNote(feedback.meta?.adminNote || "");

    if (!feedback.read) {
      handleToggleRead(feedback._id, false);
    }
  };

  const handleSaveNote = async () => {
    if (!selectedFeedback) return;

    try {
      await axios.patch(
        `${API_URL}/api/adminfeedback/${selectedFeedback._id}`,
        { note: adminNote },
        {
          headers: {
            Authorization: `Bearer ${getAuthToken()}`,
          },
        }
      );
      setSelectedFeedback(null);
      fetchFeedbacks();
    } catch (err) {
      setError("Failed to save note");
    }
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleString();
  };

  const truncateContent = (content, maxLength = 100) => {
    if (content.length <= maxLength) return content;
    return content.substring(0, maxLength) + "...";
  };

  const appData = React.useMemo(() => {
    const counts = {};
    feedbacks.forEach((fb) => {
      counts[fb.appName] = (counts[fb.appName] || 0) + 1;
    });
    return Object.entries(counts).map(([name, value]) => ({ name, value }));
  }, [feedbacks]);

  const categoryData = React.useMemo(() => {
    const counts = {};
    feedbacks.forEach((fb) => {
      counts[fb.category] = (counts[fb.category] || 0) + 1;
    });
    return Object.entries(counts).map(([name, value]) => ({ name, value }));
  }, [feedbacks]);

  const monthlyData = React.useMemo(() => {
    const counts = {};
    feedbacks.forEach((fb) => {
      const month = new Date(fb.createdAt).toLocaleString("default", {
        month: "short",
      });
      counts[month] = (counts[month] || 0) + 1;
    });
    return Object.entries(counts).map(([month, count]) => ({ month, count }));
  }, [feedbacks]);

  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchFeedbackById = async (id) => {
    try {
      const response = await axios.get(`${API_URL}/api/adminfeedback/${id}`, {
        headers: { Authorization: `Bearer ${getAuthToken()}` },
      });
      if (response.data?.data) {
        handleViewDetails(response.data.data);
      }
    } catch (err) {
      setError("Failed to fetch feedback details");
    }
  };

  useEffect(() => {
    if (feedbackIdFromUrl) {
      fetchFeedbackById(feedbackIdFromUrl);
    }
  }, [feedbackIdFromUrl]);

  const getAuthToken = () => {
    return localStorage.getItem("accessToken") || "";
  };

  const fetchUserDetails = async (userId) => {
    if (!userId) {
      setUserDetailsError("No user ID available");
      return;
    }

    setUserDetailsLoading(true);
    setUserDetailsError("");
    setUserDetails(null);

    try {
      const response = await axios.post(
        "https://accounts.clikkle.com:5000/api/auth/get_user_profile",
        { id: userId }
      );

      if (response.data?.success && response.data?.user) {
        setUserDetails(response.data.user);
      } else {
        setUserDetailsError("User not found");
      }
    } catch (err) {
      setUserDetailsError(
        err.response?.data?.message || "Failed to fetch user details"
      );
    } finally {
      setUserDetailsLoading(false);
    }
  };

  const handleDeleteClick = (id) => {
    setDeletingId(id);
    setDeleteConfirmOpen(true);
  };

  // cancel deletion
  const handleCancelDelete = () => {
    setDeletingId(null);
    setDeleteConfirmOpen(false);
  };

  // confirm deletion (calls DELETE, refreshes list, handles loading/error)
  const handleConfirmDelete = async () => {
    if (!deletingId) return;

    setDeleting(true);
    setError("");

    try {
      await axios.delete(`${API_URL}/api/adminfeedback/${deletingId}`, {
        headers: { Authorization: `Bearer ${getAuthToken()}` },
      });

      // close dialog + reset state
      setDeleteConfirmOpen(false);
      // if the details dialog is open for the same item, close it
      if (selectedFeedback?._id === deletingId) setSelectedFeedback(null);
      setDeletingId(null);

      // refresh table
      fetchFeedbacks();
    } catch (err) {
      setError(err.response?.data?.error || "Failed to delete feedback");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <Box
        sx={{
          boxShadow: "none",
          border: `1px solid ${theme.palette.divider}`,
          padding: theme.spacing(4),
          borderRadius: 2,
          minHeight: "80vh",
          color: theme.palette.text.primary,
        }}
        className="p-4 sm:p-6"
      >
        <Typography variant="h4" fontWeight="bold">
          Feedback Management
        </Typography>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
            {error}
          </Alert>
        )}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} md={4}>
            <Box sx={{ p: 2, height: 300 }}>
              <Typography variant="h6" gutterBottom>
                By App
              </Typography>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={appData}>
                  <XAxis dataKey="name" tick={false} axisLine={false} />
                  <YAxis />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "#000",
                      border: "none",
                      borderRadius: "6px",
                    }}
                    itemStyle={{ color: "#fff" }}
                    labelStyle={{ color: "#fff" }}
                  />
                  <Bar dataKey="value">
                    {appData.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={COLORS[index % COLORS.length]}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </Grid>

          <Grid item xs={12} md={4}>
            <Box sx={{ p: 2, height: 300 }}>
              <Typography variant="h6" gutterBottom>
                By Category
              </Typography>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={categoryData}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={100}
                    label
                  >
                    {categoryData.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={COLORS[index % COLORS.length]}
                      />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "#000",
                      border: "none",
                      borderRadius: "6px",
                    }}
                    itemStyle={{ color: "#fff" }}
                    labelStyle={{ color: "#fff" }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </Box>
          </Grid>

          <Grid item xs={12} md={4}>
            <Box sx={{ p: 2, height: 300 }}>
              <Typography variant="h6" gutterBottom>
                By Month
              </Typography>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={monthlyData}>
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "#000",
                      border: "none",
                      borderRadius: "6px",
                    }}
                    itemStyle={{ color: "#fff" }}
                    labelStyle={{ color: "#fff" }}
                  />
                  <Legend />
                  <Line type="monotone" dataKey="count" stroke={COLORS[2]} />
                </LineChart>
              </ResponsiveContainer>
            </Box>
          </Grid>
        </Grid>

        <Box sx={{ p: 1, mb: 2 }}>
          <Grid container spacing={1} alignItems="center" wrap="nowrap">
            <Grid item xs="auto" mt={"14px"}>
              <TextField
                size="small"
                label="Search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  endAdornment: <SearchIcon fontSize="small" />,
                }}
              />
            </Grid>

            <Grid item xs="auto">
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Category</InputLabel>
                <Select
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value)}
                  label="Category"
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="Question">Question</MenuItem>
                  <MenuItem value="Comment">Comment</MenuItem>
                  <MenuItem value="Bug">Bug</MenuItem>
                  <MenuItem value="Suggestion">Suggestion</MenuItem>
                  <MenuItem value="Rating">Rating</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs="auto">
              <FormControl size="small" sx={{ minWidth: 100 }}>
                <InputLabel>Status</InputLabel>
                <Select
                  value={readFilter}
                  onChange={(e) => setReadFilter(e.target.value)}
                  label="Status"
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="false">Unread</MenuItem>
                  <MenuItem value="true">Read</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs="auto">
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel>App Name</InputLabel>
                <Select
                  value={appNameFilter}
                  onChange={(e) => setAppNameFilter(e.target.value)}
                  label="App Name"
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="Clikkle_App">Clikkle_App</MenuItem>
                  <MenuItem value="Clikkle_App">Clikkle_Website</MenuItem>
                  <MenuItem value="Clikkle_Projects">Clikkle_Projects</MenuItem>
                  <MenuItem value="Clikkle_Hr">Clikkle_Hr</MenuItem>
                  <MenuItem value="Clikkle_eSign">Clikkle_eSign</MenuItem>
                  <MenuItem value="Clikkle_Chat">Clikkle_Chat</MenuItem>
                  <MenuItem value="Clikkle_Campaigns">
                    Clikkle_Campaigns
                  </MenuItem>
                  <MenuItem value="Clikkle_Files">Clikkle_Files</MenuItem>
                  <MenuItem value="Clikkle_MyAccounts">
                    Clikkle_MyAccounts
                  </MenuItem>
                  <MenuItem value="Clikkle_Store">Clikkle_Store</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs="auto">
              <Button
                size="small"
                variant="outlined"
                onClick={fetchFeedbacks}
                disabled={loading}
              >
                <RefreshCwIcon className="w-6" />
              </Button>
            </Grid>
          </Grid>
        </Box>

        <TableContainer component={Box}>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Time</TableCell>
                    <TableCell>App</TableCell>
                    <TableCell>Category</TableCell>
                    <TableCell>Content</TableCell>
                    <TableCell>User Email</TableCell>
                    <TableCell>User Details</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {feedbacks.map((feedback) => (
                    <TableRow key={feedback._id}>
                      <TableCell>{formatDate(feedback.createdAt)}</TableCell>
                      <TableCell>{feedback.appName}</TableCell>
                      <TableCell>
                        <Chip
                          label={feedback.category}
                          size="small"
                          color={
                            feedback.category === "Bug"
                              ? "error"
                              : feedback.category === "Suggestion"
                                ? "info"
                                : feedback.category === "Question"
                                  ? "primary"
                                  : feedback.category === "Comment"
                                    ? "secondary"
                                    : feedback.category === "Rating"
                                      ? "success"
                                      : "default"
                          }
                        />
                      </TableCell>
                      <TableCell>{truncateContent(feedback.content)}</TableCell>
                      <TableCell>{feedback.userEmail || "N/A"}</TableCell>
                      <TableCell>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() =>
                              handleUserDetailsClick(feedback.userId)
                            }
                            disabled={!feedback.userId}
                            startIcon={<PersonIcon />}
                            endIcon={<ViewIcon />}
                            sx={{
                              fontSize: "0.75rem",
                            }}
                          >
                            View
                          </Button>
                          {feedback.userId && (
                            <IconButton
                              size="small"
                              onClick={() => navigate(`/users?userId=${feedback.userId}`)}
                              sx={{ color: theme.palette.primary.main }}
                              title="Go to User Profile"
                            >
                              <ArrowForwardIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={feedback.read ? "Read" : "Unread"}
                          size="small"
                          color={feedback.read ? "default" : "primary"}
                        />
                      </TableCell>
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={() =>
                            handleToggleRead(feedback._id, feedback.read)
                          }
                          title={
                            feedback.read ? "Mark as unread" : "Mark as read"
                          }
                        >
                          {feedback.read ? <ReadIcon /> : <UnreadIcon />}
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleViewDetails(feedback)}
                          title="View details"
                        >
                          <ViewIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteClick(feedback._id)} // <-- run it here
                          title="Delete feedback"
                          disabled={deleting && deletingId === feedback._id}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <TablePagination
                component="div"
                count={total}
                page={page}
                onPageChange={(e, newPage) => setPage(newPage)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
              />
            </>
          )}
        </TableContainer>

        <Dialog
          open={!!selectedFeedback}
          onClose={() => setSelectedFeedback(null)}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: { borderRadius: 3, overflow: "hidden" },
          }}
        >
          {selectedFeedback && (
            <>
              <DialogTitle
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  borderBottom: "1px solid",
                  borderColor: "divider",
                  fontWeight: "bold",
                }}
              >
                Feedback Details
                <Chip
                  label={selectedFeedback.read ? "Read" : "Unread"}
                  size="small"
                  color={selectedFeedback.read ? "default" : "primary"}
                  variant={selectedFeedback.read ? "outlined" : "filled"}
                />
              </DialogTitle>

              <DialogContent dividers>
                <Grid container spacing={2}>
                  {[
                    { label: "App Name", value: selectedFeedback.appName },
                    { label: "Category", value: selectedFeedback.category },
                    {
                      label: "User Email",
                      value: selectedFeedback.userEmail || "N/A",
                    },
                    {
                      label: "Created At",
                      value: formatDate(selectedFeedback.createdAt),
                      full: true,
                    },
                  ].map(({ label, value, full }, i) => (
                    <Grid key={i} item xs={full ? 12 : 6}>
                      <Box
                        sx={{
                          p: 2,
                          bgcolor: "background.paper",
                          borderRadius: 2,
                          boxShadow: 1,
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{ color: "text.secondary", fontWeight: 500 }}
                        >
                          {label}
                        </Typography>
                        <Typography variant="body1" sx={{ mt: 0.5 }}>
                          {value}
                        </Typography>
                      </Box>
                    </Grid>
                  ))}

                  <Grid item xs={12}>
                    <Box
                      sx={{
                        p: 2,
                        bgcolor: "background.paper",
                        borderRadius: 2,
                        boxShadow: 1,
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{ color: "text.secondary", fontWeight: 500 }}
                      >
                        Content
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ mt: 1, whiteSpace: "pre-wrap" }}
                      >
                        {selectedFeedback.content}
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      multiline
                      rows={3}
                      label="Admin Note"
                      value={adminNote}
                      onChange={(e) => setAdminNote(e.target.value)}
                      placeholder="Add internal notes here..."
                      variant="outlined"
                    />
                  </Grid>
                </Grid>
              </DialogContent>

              <DialogActions
                sx={{ p: 2.5, borderTop: "1px solid", borderColor: "divider" }}
              >
                <Button onClick={() => setSelectedFeedback(null)}>Close</Button>
                <Button
                  onClick={() =>
                    handleToggleRead(
                      selectedFeedback._id,
                      selectedFeedback.read
                    )
                  }
                  variant="outlined"
                >
                  Mark as {selectedFeedback.read ? "Unread" : "Read"}
                </Button>
                <Button
                  onClick={handleSaveNote}
                  variant="contained"
                  disableElevation
                >
                  Save Note
                </Button>
              </DialogActions>
            </>
          )}
        </Dialog>

        <Dialog
          open={userDetailsOpen}
          onClose={handleCloseUserDetails}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: { borderRadius: 3, overflow: "hidden" },
          }}
        >
          <DialogTitle
            sx={{
              borderBottom: "1px solid",
              borderColor: "divider",
              fontWeight: "bold",
            }}
          >
            User Details
          </DialogTitle>

          <DialogContent dividers>
            {userDetailsLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
                <CircularProgress />
              </Box>
            ) : userDetailsError ? (
              <Alert severity="error" sx={{ mb: 2 }}>
                {userDetailsError}
              </Alert>
            ) : userDetails ? (
              <Box sx={{ p: 2 }}>
                <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                  <Avatar
                    src={userDetails.profilePicture}
                    sx={{ width: 80, height: 80, mr: 2 }}
                  >
                    {userDetails.firstName?.charAt(0)}
                  </Avatar>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                      {userDetails.firstName} {userDetails.lastName}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      @{userDetails.username}
                    </Typography>
                    <Chip
                      label={userDetails.role}
                      size="small"
                      color="primary"
                      sx={{ mt: 1 }}
                    />
                  </Box>
                </Box>

                <Divider sx={{ mb: 3 }} />

                <Grid container spacing={2}>
                  {[
                    { label: "Email", value: userDetails?.email },
                    {
                      label: "Recovery Email",
                      value: userDetails?.recoveryEmail,
                    },
                    { label: "Account Type", value: userDetails?.accountType },
                    { label: "Role", value: userDetails?.role },
                    {
                      label: "Verified",
                      value: userDetails?.isVerified ? "Yes" : "No",
                    },
                    {
                      label: "Auth Providers",
                      value: userDetails?.authProvider?.join(", ") || "N/A",
                    },
                    {
                      label: "User ID",
                      value: userDetails?._id || "N/A",
                    },
                    {
                      label: "Created At",
                      value: formatDate(userDetails?.createdAt),
                      full: true,
                    },
                    {
                      label: "Last Updated",
                      value: formatDate(userDetails?.updatedAt),
                      full: true,
                    },
                  ].map(({ label, value, full }, i) => (
                    <Grid key={i} item xs={full ? 12 : 6}>
                      <Box
                        sx={{
                          p: 2,
                          bgcolor: "background.default",
                          borderRadius: 2,
                          border: "1px solid",
                          borderColor: "divider",
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{ color: "text.secondary", fontWeight: 500 }}
                        >
                          {label}
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 0.5 }}>
                          {value || "N/A"}
                        </Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>

                {userDetails?.business && (
                  <>
                    <Divider sx={{ my: 3 }} />
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: "bold" }}>
                      Business Information
                    </Typography>
                    <Box
                      sx={{
                        p: 2,
                        bgcolor: "background.default",
                        borderRadius: 2,
                        border: "1px solid",
                        borderColor: "divider",
                      }}
                    >
                      <Typography variant="body2">
                        {JSON.stringify(userDetails?.business, null, 2)}
                      </Typography>
                    </Box>
                  </>
                )}
              </Box>
            ) : null}
          </DialogContent>

          <DialogActions
            sx={{ p: 2.5, borderTop: "1px solid", borderColor: "divider" }}
          >
            <Button onClick={handleCloseUserDetails} variant="outlined">
              Close
            </Button>
          </DialogActions>
        </Dialog>
      </Box>

      <Dialog open={deleteConfirmOpen} onClose={handleCancelDelete}>
        <DialogTitle>Delete feedback</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this feedback? This action cannot be
            undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete} disabled={deleting}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            variant="contained"
            color="error"
            disabled={deleting}
          >
            {deleting ? <CircularProgress size={18} /> : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default Feedback;
